import { URLS } from "Common/urls";
import { useSnackbar } from "Components/Snackbar";
import RestService from "Services/RestService";
import { StorageProvider } from "Utils/data";

const { createContext, useState, useContext, useEffect } = require("react");

const AUTH_USER = "atease.auth_user"
const AUTH_TOKEN = "atease.auth_token"

const getUser = () => StorageProvider.get(AUTH_USER)
const saveUser = user => StorageProvider.set(AUTH_USER, user)
const removeUser = () => StorageProvider.remove(AUTH_USER)

export const getToken = () => StorageProvider.get(AUTH_TOKEN)
const saveToken = token => StorageProvider.set(AUTH_TOKEN, token)
const removeToken = () => StorageProvider.remove(AUTH_TOKEN)

const SysBrewAuthContext = createContext()

const SysBrewAuthProvider = ({ children }) => {
    const [user, setUser] = useState({})
    const [isLoading, setLoading] = useState(false)
    const [isAuthenticated, setAuthenticated] = useState(false)
    const [isMobileValid, setMobileValid] = useState(false)
    const [isProfileComplete, setProfileComplete] = useState(false)
    const [token, setToken] = useState(undefined)
    const [error, setError] = useState(false)
    const [errorDescription, setErrorDescription] = useState(undefined)
    const { showError } = useSnackbar()

    useEffect(() => {
        if (getToken()) {
            revalidateToken()
        }
    }, [])

    const revalidateToken = (showLoader = true) => {
        if (showLoader) {
            setLoading(true)
        }
        RestService.post(URLS.AUTHENTICATE_TOKEN)
            .then(({ statusCode, data }) => {
                if (statusCode === 200) {
                    setToken(data['atease-x-token']);
                    saveToken(data['atease-x-token']);
                    setAuthenticated(true);
                    const user = getUser() || {};
                    user.name = data.user.name;
                    user.email = data.user.email;
                    user.mobile = data.user.mobile;
                    user.profile_image_url = data.user.profile_image_url;
                    saveUser(user);
                    setUser(user);
                    setError(false);
                } else {
                    setAuthenticated(false);
                    setError(true);
                }
            })
            .catch((error) => {
                setAuthenticated(false)
                setError(true)
            })
            .finally(() => {
                if (showLoader) {
                    setLoading(false)
                }
            })
    }

    const logout = ({ returnTo = undefined }) => {
        removeUser()
        setToken(undefined)
        setUser({})
        setAuthenticated(false)
        setMobileValid(false)
    }

    const validateMobile = (mobile, redirectTo) => {
        setLoading(true)
        RestService.post(URLS.CHECK_MOBILE, { mobile })
            .then(({ statusCode, data, error }) => {
                if (statusCode === 200) {
                    setMobileValid(true)
                    setUser({ mobile })
                    setError(false)
                    setErrorDescription(undefined)
                    setProfileComplete(data.profile_complete)
                } else {
                    setMobileValid(false)
                    setError(true)
                    setErrorDescription(error.description)
                }
            })
            .catch((error) => {
                console.log(error)
                setErrorDescription(error && error.error && error.error.description)
                setError(true)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const completeProfile = (body, redirectTo) => {
        setLoading(true)
        return RestService.post(URLS.COMPLETE_PROFILE, body, true)
            .then(({ statusCode, data }) => {
                if (statusCode === 200) {
                    setToken(data['atease-x-token'])
                    saveToken(data['atease-x-token'])
                    setUser(data.user)
                    delete data['atease-x-token']
                    saveUser(data.user)
                    setProfileComplete(true)
                    setAuthenticated(true)
                    setError(false)
                    setErrorDescription(undefined)
                    return Promise.resolve()
                } else {
                    setAuthenticated(false)
                    setError(true)
                    return Promise.reject()
                }
            })
            .catch((error) => {
                console.log(error)
                setAuthenticated(false)
                if (error.statusCode) {
                    showError(error && error.error && error.error.description)
                    return Promise.resolve()
                } else {
                    setErrorDescription(error && error.error && error.error.description)
                    setError(true)
                }
                return Promise.reject()
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const loginWithRedirect = (mobile, email, password, redirectTo) => {
        setLoading(true)
        RestService.post(URLS.LOGIN, { mobile, email, password })
            .then(({ statusCode, data }) => {
                if (statusCode === 200) {
                    setToken(data['atease-x-token'])
                    saveToken(data['atease-x-token'])
                    setUser(data.user)
                    delete data['atease-x-token']
                    saveUser(data.user)
                    setAuthenticated(true)
                    setError(false)
                    setErrorDescription(undefined)
                } else {
                    setAuthenticated(false)
                    setError(true)
                }
            })
            .catch((error) => {
                console.log(error)
                setErrorDescription(error && error.error && error.error.description)
                setAuthenticated(false)
                setError(true)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    return (
        <SysBrewAuthContext.Provider value={{
            user,
            setUser,
            isLoading,
            setLoading,
            isMobileValid,
            setMobileValid,
            validateMobile,
            isProfileComplete,
            setProfileComplete,
            completeProfile,
            isAuthenticated,
            setAuthenticated,
            logout,
            token,
            loginWithRedirect,
            error,
            errorDescription,
            revalidateToken
        }}>
            {children}
        </SysBrewAuthContext.Provider>
    )
}

const useSysBrewAuth = () => {
    const { user,
        isLoading,
        isAuthenticated,
        isMobileValid,
        isProfileComplete,
        logout,
        token,
        error,
        validateMobile,
        completeProfile,
        loginWithRedirect,
        errorDescription,
        revalidateToken
    } = useContext(SysBrewAuthContext)


    return {
        user,
        isLoading,
        isAuthenticated,
        isMobileValid,
        isProfileComplete,
        token,
        logout,
        error,
        validateMobile,
        completeProfile,
        loginWithRedirect,
        errorDescription,
        removeToken,
        removeUser,
        revalidateToken,
    }
}

export { SysBrewAuthProvider, useSysBrewAuth }
